import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { vatMessages } from 'dibs-vat-utils/exports/vatMessages.ts';

import { useItemPriceData } from '../hooks/useItemPriceData.ts';

import { type IncludedLabels_item$key } from './__generated__/IncludedLabels_item.graphql.ts';
import { type IncludedLabels_displayPrice$key } from './__generated__/IncludedLabels_displayPrice.graphql.ts';

import dibsCss from 'dibs-css';

const itemFragment = graphql`
    fragment IncludedLabels_item on Item {
        ...useItemPriceData_item
        attributes {
            framingStatus {
                value
            }
        }
    }
`;

const displayPriceFragment = graphql`
    fragment IncludedLabels_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...useItemPriceData_displayPrice
    }
`;

const FRAMED = 'Framed';

export const IncludedLabels: FC<{
    item?: IncludedLabels_item$key | null;
    displayPrice?: IncludedLabels_displayPrice$key | null;
    currency: string;
}> = ({ item: itemRef, displayPrice: displayPriceRef, currency }) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);

    const frameIncluded = item?.attributes?.framingStatus?.[0]?.value === FRAMED;

    const data = useItemPriceData({ item, displayPrice, currency });
    const { vatIncluded, priceBookName = null } = data || {};

    if (!frameIncluded && !vatIncluded) {
        return null;
    }

    return (
        <span
            className={classnames(
                dibsCss.sassyFontBodySmall,
                dibsCss.alignTextBottom,
                dibsCss.textSatan
            )}
        >
            <FormattedMessage
                id="IncludedLabels.labels"
                defaultMessage="{frameIncluded, select, true {Frame Included} other {}}{showDivider, select, true { | } other {}}{vatIncluded, select, true {{vatLabel}} other {}}"
                values={{
                    vatIncluded,
                    vatLabel: vatMessages.included({ priceBookName }),
                    showDivider: vatIncluded && frameIncluded,
                    frameIncluded,
                }}
            />
        </span>
    );
};
